<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import TruncatableSpan from "@/components/ui/TruncatableSpan.vue";
import { type WorkspaceDto } from "@/dto";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { formatDatetime } from "@/services/utils";

const api = useApi();
const router = useRouter();
const isLoading = ref(true);
const workspaces = ref<WorkspaceDto[]>([]);

async function refreshWorkspaces() {
  isLoading.value = true;
  workspaces.value = await api.listWorkspaces();
  if (workspaces.value.length > 0) {
    isLoading.value = false;
  } else {
    router.push({ name: RouteNames.Onboarding });
  }
}

onMounted(async () => {
  await refreshWorkspaces();
});
</script>

<template>
  <DefaultPageLayout :breadcrumb-items="[{ label: 'Workspaces' }]">
    <div class="workspaces-list-view">
      <Transition name="fade" mode="out-in">
        <div v-if="isLoading" class="loading">
          <SkoreSpinner />
        </div>

        <div v-else class="wrapper">
          <header>
            <div>
              <h1 class="text-2xl-medium">Workspaces</h1>
              <h2 class="text-base-regular">Browse the workspaces you have access to.</h2>
            </div>

            <div class="actions">
              <RouterLink
                :to="{ name: RouteNames.Onboarding, query: { create: 'true' } }"
                class="skore-button primary"
              >
                <i class="icon left icon-plus" />
                <span class="label">Create a workspace</span>
              </RouterLink>
            </div>
          </header>

          <table class="tabular-list">
            <thead>
              <tr>
                <th class="text-base-regular">Workspace public id</th>
                <th class="text-base-regular">Members</th>
                <th class="text-base-regular">Created</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="workspace in workspaces" :key="workspace.id">
                <td>
                  <RouterLink
                    :to="{
                      name: RouteNames.Projects,
                      params: { workspacePublicId: workspace.public_id },
                    }"
                  >
                    <span class="name text-base-regular">
                      <TruncatableSpan :text="workspace.public_id" with-end />
                    </span>
                  </RouterLink>
                </td>

                <td>{{ workspace.members?.length ?? 0 }}</td>

                <td class="cell-with-actions">
                  <div>{{ formatDatetime(workspace.created_at, false) }}</div>
                  <RouterLink
                    :to="{
                      name: RouteNames.WorkspaceDetail,
                      params: { workspaceId: workspace.id },
                    }"
                    class="skore-button ghost link"
                  >
                    <i class="icon left icon-gear" />
                  </RouterLink>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Transition>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.workspaces-list-view {
  .loading {
    margin-top: 40dvh;
    text-align: center;
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    padding: calc(var(--numbers-32) - var(--numbers-12)) 0 var(--numbers-32) 0;
    gap: var(--numbers-32);

    header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;

      h1 {
        color: var(--content-primary);
      }

      h2 {
        color: var(--content-tertiary);
      }
    }

    .cell-with-actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;

      .skore-button {
        --color: var(--content-tertiary);
      }
    }

    a {
      text-decoration: none;
    }

    thead {
      tr {
        color: var(--content-secondary);
      }
    }

    tbody {
      tr {
        color: var(--content-tertiary);
      }
    }

    .name {
      --icon-size: var(--numbers-24);

      position: relative;
      padding-left: calc(var(--icon-size) + var(--numbers-8));
      color: var(--content-primary);

      &::before {
        position: absolute;
        top: -7%;
        left: 0;
        overflow: hidden;
        width: var(--icon-size);
        height: var(--icon-size);
        border: solid var(--stroke-width-md) var(--border-tertiary);
        border-radius: var(--icon-size);
        background-image: url("@/assets/images/skore-icon.svg");
        background-position: 60% 50%;
        background-repeat: no-repeat;
        background-size: 70%;
        content: "";
      }
    }
  }
}
</style>
